import clsx from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import Loader from '../Loader';

export enum ButtonType {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Tertiary = 'Tertiary',
  Outlined = 'Outlined',
  IconButton = 'IconButton',
  Tab = 'Tab',
  Link = 'Link',
}

interface IButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  className?: string;
  children?: any;
  type?: ButtonType | keyof typeof ButtonType;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onSubmit?: React.FormEventHandler<HTMLButtonElement> | undefined;
  iconImage?: any;
  iconRight?: any;
  style?: React.CSSProperties | undefined;
  disabled?: boolean;
  buttonType?: 'submit' | 'button' | 'reset' | undefined;
  loading?: boolean;
  dataTip?: string | undefined | boolean;
  dataFor?: string;
  setShowTooltip?: (showTooltip: boolean) => void;
  id?: string;
  size?: 'sm' | 'xs' | 'md'
};

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(({ 
  children,
  className,
  type = "Primary",
  onClick,
  onSubmit,
  iconImage,
  iconRight,
  style,
  disabled = false,
  buttonType = 'submit',
  loading,
  dataTip,
  dataFor,
  id,
  setShowTooltip,
  size = 'sm'
}, ref) => {

  if (disabled) {
    type = ButtonType.Secondary;
  }

  const classes = twMerge(
    clsx('flex justify-center items-center px-4 transition transition-colors duration-300', {
      'bg-btn-primary hover:bg-btn-overlay-primary rounded-full': type === ButtonType.Primary,
      'bg-secondary text-primary rounded-full hover:bg-secondary/70 disabled:text-secondary disabled:border-primary disabled:border-1 disabled:bg-transparent': type === ButtonType.Secondary,
      'bg-btn-tertiary hover:bg-btn-overlay-secondary text-primary rounded-full': type === ButtonType.Tertiary,
      'bg-secondary text-primary h-8 w-11 rounded-sm': type === ButtonType.Tab,
      'rounded-full w-16 flex justify-center items-center': type === ButtonType.IconButton,
      'bg-transparent text-primary px-0 h-auto': type === ButtonType.Link,
      'outline outline-white/30 text-primary rounded-full': type === ButtonType.Outlined,
      'cursor-not-allowed': disabled,
      'h-10 text-sm': size === 'sm',
      'h-12 text-sm px-8 py-4': size === 'md',
      'h-8 text-xs': size === 'xs',
    }),
    className
  );

  let mouseActions = {};

  if (setShowTooltip) {
    mouseActions = {
      onMouseEnter: () => setShowTooltip!(true),
      onMouseLeave: () => {
        setShowTooltip!(false);
        setTimeout(() => setShowTooltip!(true), 50);
      },
    };
  }

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      onSubmit={onSubmit}
      style={style}
      type={buttonType}
      data-tip={dataTip}
      data-for={dataFor}
      id={id}
      {...mouseActions}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {iconImage && <img alt="icon" src={iconImage} className="mr-3" />}
          {children}
          {iconRight && <div className="ml-3">{iconRight}</div>}
        </>
      )}
    </button>
  );
})

Button.displayName = "Button";

export default Button;
